import { css, cx } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Callout, Collapse, EmptyStateDefaultIllustration, Button } from "@octopusdeploy/design-system-components";
import { FilterIcon } from "@octopusdeploy/design-system-icons";
import { FilterSearchBox } from "@octopusdeploy/design-system-octopus-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentResource, EventCategoryResource, EventGroupResource, MachineFilterResource, ProjectResource, Repository, ResourceCollection, RunbookResource, RunRunbookActionResource, TagSetResource, TriggerActionResource, TriggerFilterResource, TriggerResource, TriggerScheduleResource, } from "@octopusdeploy/octopus-server-client";
import { Permission, TriggerActionCategory, TriggerActionType, TriggerFilterType, triggerIdIs } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type * as React from "react";
import { useSelector } from "react-redux";
import MediaQuery from "react-responsive";
import RunbooksOnboarding from "~/areas/projects/components/Runbooks/RunbooksOnboarding";
import { TriggerNameAndLink } from "~/areas/projects/components/Triggers/Feed/FeedTriggerDescriptionHelper";
import TriggerDescription from "~/areas/projects/components/Triggers/TriggerDescription";
import { TriggerType } from "~/areas/projects/components/Triggers/TriggerType";
import { loadAllTriggerRunbooks } from "~/areas/projects/components/Triggers/loadAllTriggerRunbooks";
import { FilterBuilder } from "~/areas/tenants/components/Filtering/FilterBuilder/FilterBuilder";
import { createSingleValueFilter, createTagSetFilters, FilterEntity, FilterMultiplicity, FilterOption, getIncludedTagSetValues, getSingleSelectValue } from "~/areas/tenants/components/Filtering/FilterBuilder/filterBuilderTypes";
import type { Filter, FilterValue } from "~/areas/tenants/components/Filtering/FilterBuilder/filterBuilderTypes";
import { repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import { EmptyPage } from "~/components/EmptyPage/EmptyPage";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import PagingDataTable from "~/components/PagingDataTable";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { getAllByScopes } from "~/components/tagSets";
import type { ProjectPageLoaderContext } from "~/routing/pageRegistrations/ProjectPageRegistration";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import ScheduledTriggerDescriptionHelper from "~/utils/ScheduledTriggerDescriptionHelper";
export async function runbookTriggersPageLoader(repository: Repository, context: ProjectPageLoaderContext): Promise<RunbookTriggersPageLoaderData> {
    const projectContext = await context.projectContext;
    const { model: project } = projectContext.state;
    const environmentsPromise = repository.Environments.all();
    const categoriesPromise = repository.Events.categories({ appliesTo: "Machine" });
    const groupsPromise = repository.Events.groups({ appliesTo: "Machine" });
    const triggersPromise = repository.Projects.getTriggers(project, 0, 30, undefined, TriggerActionCategory.Runbook);
    const runbooksPromise = loadAllTriggerRunbooks(project);
    const tagSetsPromise = getAllByScopes(["Runbook"]);
    return {
        triggersResponse: await triggersPromise,
        environments: await environmentsPromise,
        categories: await categoriesPromise,
        groups: await groupsPromise,
        project,
        runbooksInProject: await runbooksPromise,
        tagSets: await tagSetsPromise,
    };
}
interface RunbookTriggersPageLoaderData {
    project: ProjectResource;
    triggersResponse: ResourceCollection<TriggerResource>;
    environments: EnvironmentResource[];
    categories: EventCategoryResource[];
    groups: EventGroupResource[];
    runbooksInProject: RunbookResource[];
    tagSets: TagSetResource[];
}
interface RunbookTriggersPageInternalState extends DataBaseComponentState {
    triggersResponse: ResourceCollection<TriggerResource>;
    open: boolean;
    filter: RunbookTriggersFilter;
    isSearching: boolean;
    filtersExpanded: boolean;
}
interface RunbookTriggersFilter {
    searchText: string;
    runbookId: string | undefined;
    runbookTags: string[];
}
interface GlobalConnectedProps {
    isMultiTenancyEnabled?: boolean;
}
type RunbookTriggersPageInternalProps = RunbookTriggersPageProps & GlobalConnectedProps;
class RunbookTriggersPageInternal extends DataBaseComponent<RunbookTriggersPageInternalProps, RunbookTriggersPageInternalState> {
    constructor(props: RunbookTriggersPageInternalProps) {
        super(props);
        this.state = {
            triggersResponse: this.props.loaderData.triggersResponse,
            open: false,
            filter: createDefaultFilter(),
            isSearching: false,
            filtersExpanded: false,
        };
    }
    filterTriggers = (_: string, trigger: TriggerResource) => {
        const { searchText } = this.state.filter;
        return searchText === "" || trigger.Name.toLowerCase().includes(searchText.toLowerCase());
    };
    render() {
        const content = (<MediaQuery maxWidth={600}>
                {(small) => (<MediaQuery minWidth={901}>
                        {(large) => (<PagingDataTable<TriggerResource> tableClassName={small ? "" : emotionStyles.tableCellHeightHack} headerColumns={this.buildDataTableColumnHeaders(getBreakpoint(small, large))} initialData={this.state.triggersResponse} additionalRequestParams={new Map([["triggerActionCategory", [TriggerActionCategory.Runbook]]])} onRow={(item) => this.buildDataTableColumns(item, getBreakpoint(small, large))} onFilter={this.filterTriggers} onEmpty={() => (<EmptyPage image={<div className={emotionStyles.emptyStateIllustration}>
                                                <EmptyStateDefaultIllustration />
                                            </div>} title={"You have no triggers available yet"} description={"Add a trigger to get started"}/>)}></PagingDataTable>)}
                    </MediaQuery>)}
            </MediaQuery>);
        const hasRunbook = this.props.loaderData.runbooksInProject.length !== 0;
        const isFiltering = this.state.filter.searchText !== "" || !!this.state.filter.runbookId || this.state.filter.runbookTags.length > 0;
        // Only show onboarding if there are no triggers when no filters are applied
        const showOnboarding = (this.props.loaderData.runbooksInProject.length === 0 || this.state.triggersResponse.TotalResults === 0) && !isFiltering;
        const project = this.props.loaderData.project;
        let primaryAction: PrimaryPageAction | undefined = undefined;
        let primaryButton: React.ReactNode | undefined = undefined;
        if (project) {
            const addScheduledTriggerPageAction: PrimaryPageAction = {
                type: "navigate",
                label: "Add Scheduled Trigger",
                path: links.createRunbookScheduledTriggerPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }),
                hasPermissions: isAllowed({
                    permission: Permission.TriggerCreate,
                    project: project.Id,
                }),
            };
            const addScheduledTriggerButton = (<PermissionCheck permission={Permission.TriggerCreate} project={project.Id} tenant="*">
                    <Button importance="primary" label="Add Scheduled Trigger" href={links.createRunbookScheduledTriggerPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug })}/>
                </PermissionCheck>);
            const goToRunbooksButton = (<PermissionCheck permission={Permission.RunbookEdit} project={project.Id} tenant="*">
                    <Button importance="secondary" label="Go to Runbooks" href={links.runbooksPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug })}/>
                </PermissionCheck>);
            primaryButton = !hasRunbook ? goToRunbooksButton : addScheduledTriggerButton;
            primaryAction = addScheduledTriggerPageAction;
        }
        if (showOnboarding) {
            return (<PageContent header={{ title: runbookTriggersPageTitle }} busy={this.state.busy} errors={this.errors} overrideFullWidth={true}>
                    <RunbooksOnboarding actionButtons={primaryButton} notification={!hasRunbook ? (<Callout title="Runbook required." type={"information"}>
                                    <PermissionCheck permission={Permission.RunbookEdit} project={project.Id} tenant="*">
                                        Before you can schedule a runbook trigger, you will need to create your runbook.
                                    </PermissionCheck>
                                </Callout>) : undefined}/>
                </PageContent>);
        }
        const nonFreeTextTagSets = this.props.loaderData.tagSets.filter((ts) => ts.Type !== "FreeText");
        const runbookTagFilters = createTagSetFilters(nonFreeTextTagSets, this.state.filter.runbookTags, []);
        const allFilters: Filter[] = [
            createSingleValueFilter(FilterEntity.Runbook, [{ option: FilterOption.Is, initialValue: this.state.filter.runbookId || undefined }], FilterMultiplicity.OncePerOption, this.props.loaderData.runbooksInProject, "By specific runbook"),
            ...runbookTagFilters,
        ];
        return (<PageContent header={{ title: runbookTriggersPageTitle, primaryAction }} filters={{
                inputs: [
                    <section className={emotionStyles.filterSection} aria-label="runbook-trigger-filter-selection">
                            <div className={emotionStyles.filterControlsLeft}>
                                <FilterSearchBox placeholder="Filter by name..." value={this.state.filter.searchText} autoFocus={true} onChange={(searchText) => this.setFilterState({ searchText }, async () => await this.onFilterChange())}/>
                                {(this.props.loaderData.runbooksInProject.length > 0 || nonFreeTextTagSets.length > 0) && (<Button label={`${this.state.filtersExpanded ? "Hide" : "Show"} advanced filters`} icon={<FilterIcon size={20}/>} onClick={() => this.setState({ filtersExpanded: !this.state.filtersExpanded })} importance="quiet"/>)}
                            </div>
                            <Collapse in={this.state.filtersExpanded} timeout="auto" unmountOnExit>
                                <div className={emotionStyles.advancedFiltersContainer}>
                                    <div className={emotionStyles.filterBuilder}>
                                        <FilterBuilder filters={allFilters} onChange={(filterValues: FilterValue[]) => {
                            const runbookId = getSingleSelectValue(filterValues, FilterEntity.Runbook, FilterOption.Is);
                            const tags = getIncludedTagSetValues(filterValues);
                            this.setFilterState({ runbookId, runbookTags: tags }, async () => await this.onFilterChange());
                        }} display="full"/>
                                    </div>
                                </div>
                            </Collapse>
                        </section>,
                ],
            }} busy={this.state.busy} errors={this.errors} overrideFullWidth={true}>
                {content}
            </PageContent>);
    }
    async reloadTriggers() {
        const project = this.props.loaderData.project;
        await this.doBusyTask(async () => this.setState({
            triggersResponse: await repository.Projects.getTriggers(project, 0, 30, undefined, TriggerActionCategory.Runbook, this.state.filter.runbookId ? [this.state.filter.runbookId] : undefined, this.state.filter.searchText, this.state.filter.runbookTags.length ? this.state.filter.runbookTags : undefined),
        }));
    }
    private async onFilterChange() {
        this.setState({ isSearching: true }, async () => {
            await this.reloadTriggers();
            this.setState({ isSearching: false });
        });
    }
    private setFilterState<K extends keyof RunbookTriggersFilter>(state: Pick<RunbookTriggersFilter, K>, callback?: () => void) {
        this.setState((prev) => ({
            filter: { ...prev.filter, ...state },
        }), callback);
    }
    private getOverflowMenuItems(trigger: TriggerResource) {
        const triggerEditPermission = { permission: Permission.TriggerEdit, project: this.props.loaderData.project.Id };
        const menuItems = [!triggerIdIs(trigger, "built-in-feed") && OverflowMenuItems.item(trigger.IsDisabled ? "Enable" : "Disable", () => (trigger.IsDisabled ? this.enable(trigger) : this.disable(trigger)), triggerEditPermission)] as const;
        return menuItems.filter((val): val is NonNullable<Exclude<typeof val, false>> => !!val);
    }
    private async enable(trigger: TriggerResource) {
        trigger.IsDisabled = false;
        await this.saveTrigger(trigger);
    }
    private async disable(trigger: TriggerResource) {
        trigger.IsDisabled = true;
        await this.saveTrigger(trigger);
    }
    private async saveTrigger(trigger: TriggerResource) {
        const isSuccess = await this.doBusyTask(async () => {
            await repository.ProjectTriggers.modify(trigger);
            const triggersResponse = await repository.Projects.getTriggers(this.props.loaderData.project, 0, 30, undefined, TriggerActionCategory.Runbook);
            this.setState({
                triggersResponse,
            });
        });
        if (!isSuccess) {
            await this.doBusyTask(async () => this.setState({
                triggersResponse: await repository.Projects.getTriggers(this.props.loaderData.project, 0, 30, undefined, TriggerActionCategory.Runbook),
            }), { preserveCurrentErrors: true, timeOperationOptions: timeOperationOptions.forInitialLoad() });
        }
    }
    private buildDataTableColumnHeaders(screenSize: "sm" | "md" | "lg") {
        const columnHeaders = ["Name"];
        if (screenSize !== "sm") {
            columnHeaders.push("Type");
        }
        if (screenSize === "lg") {
            columnHeaders.push("Trigger action");
        }
        if (screenSize !== "sm") {
            columnHeaders.push("");
        }
        return columnHeaders;
    }
    private buildDataTableColumns(trigger: TriggerResource, screenSize: "sm" | "md" | "lg") {
        const columns = [<TriggerNameAndLink trigger={trigger} projectSlug={this.props.projectSlug}/>];
        if (screenSize !== "sm") {
            columns.push(<TriggerType trigger={trigger}/>);
        }
        if (screenSize === "lg") {
            columns.push(this.buildTriggerDescription(trigger));
        }
        if (screenSize !== "sm") {
            columns.push(this.getOverflowMenu(trigger));
        }
        return columns.map((column) => this.wrapWithStyles(column, trigger));
    }
    private wrapWithStyles(children: React.ReactNode, trigger: TriggerResource) {
        let cellStyle = emotionStyles.verticallyCenter;
        if (trigger.IsDisabled) {
            cellStyle = cx(cellStyle, emotionStyles.disabled);
        }
        return <div className={cellStyle}>{children}</div>;
    }
    private buildTriggerDescription(trigger: TriggerResource) {
        if (isMachineFilterResource(trigger.Filter)) {
            const filter = trigger.Filter;
            const description = new TriggerDescription(filter, this.props.loaderData.environments, this.props.loaderData.categories, this.props.loaderData.groups);
            return description.buildDescription();
        }
        const scheduleDescription = isScheduleTriggerResource(trigger.Filter) ? ScheduledTriggerDescriptionHelper.getScheduleDescription(trigger.Filter) : undefined;
        if (!isRunRunbookAction(trigger.Action)) {
            return <></>;
        }
        const actionDescription = ScheduledTriggerDescriptionHelper.getActionDescription(trigger.Action, this.props.loaderData.environments, this.props.loaderData.runbooksInProject, [], this.props.loaderData.tagSets);
        return (<span>
                {actionDescription}
                {scheduleDescription}.
            </span>);
    }
    private getOverflowMenu(trigger: TriggerResource) {
        return <OverflowMenu menuItems={this.getOverflowMenuItems(trigger)}/>;
    }
    static displayName = "RunbookTriggersPageInternal";
}
function isRunRunbookAction(action: TriggerActionResource): action is RunRunbookActionResource {
    return action.ActionType === TriggerActionType.RunRunbook;
}
function isMachineFilterResource(triggerFilter: TriggerFilterResource): triggerFilter is MachineFilterResource {
    return triggerFilter.FilterType === TriggerFilterType.MachineFilter;
}
function isScheduleTriggerResource(triggerFilter: TriggerFilterResource): triggerFilter is TriggerScheduleResource {
    switch (triggerFilter.FilterType) {
        case TriggerFilterType.OnceDailySchedule:
        case TriggerFilterType.ContinuousDailySchedule:
        case TriggerFilterType.DaysPerMonthSchedule:
        case TriggerFilterType.CronExpressionSchedule:
            return true;
    }
    return false;
}
function createDefaultFilter(): RunbookTriggersFilter {
    return {
        searchText: "",
        runbookId: "",
        runbookTags: [],
    };
}
interface RunbookTriggersPageProps {
    spaceId: string;
    projectSlug: string;
    loaderData: RunbookTriggersPageLoaderData;
}
export const RunbookTriggersPage: React.FC<RunbookTriggersPageProps> = (props) => {
    const isMultiTenancyEnabled = useSelector(isMultiTenancyEnabledSelector);
    return <RunbookTriggersPageInternal isMultiTenancyEnabled={isMultiTenancyEnabled} {...props}/>;
};
RunbookTriggersPage.displayName = "RunbookTriggersPage"
const isMultiTenancyEnabledSelector = (state: GlobalState) => state.configurationArea.currentSpace.isMultiTenancyEnabled;
const getBreakpoint = (small: boolean, large: boolean) => {
    if (small)
        return "sm";
    if (large)
        return "lg";
    return "md";
};
const emotionStyles = {
    // This is required so we can set the height of a cell to 100% so we can center the content
    tableCellHeightHack: css({ height: "1px" }),
    verticallyCenter: css({ height: "100%", display: "flex", alignItems: "center" }),
    disabled: css({ color: themeTokens.color.text.disabled }),
    emptyStateIllustration: css({ height: 300 }),
    filterSection: css({ width: "100%", marginBottom: space[16] }),
    filterControlsLeft: css({ display: "flex", gap: space[16], flexWrap: "wrap", alignItems: "center" }),
    advancedFiltersContainer: css({ paddingTop: space[16] }),
    filterBuilder: css({ display: "flex", gap: space[16] }),
};
export const runbookTriggersPageTitle = "Runbook Triggers";
